/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */
import { useMemo, useState } from 'react';
import { Bot, Flame, Gauge } from 'lucide-react';

const CARD = 'border-3 border-black rounded-xl';
const CHIP = 'px-3 py-1 bg-white text-black font-bold text-xs tracking-wide border-2 border-black rounded-lg';
const TABS = [
  { id: 'overview', label: 'گزارش کلی' },
  { id: 'nutrition', label: 'راهنمای تغذیه' },
  { id: 'definitions', label: 'تعاریف' }
];

function formatNumber(value) {
  return Math.round(value).toLocaleString('en-US');
}

function formatRange(weight, min, max) {
  return `${formatNumber(weight * min)} – ${formatNumber(weight * max)}`;
}

function fatRange(calories) {
  return `${formatNumber(calories * 0.2 / 9)} – ${formatNumber(calories * 0.3 / 9)}`;
}

function metabolicParagraph(bmi, bmr, tdee) {
  if (bmi <= 0 || bmr <= 0 || tdee <= 0) {
    return 'برای تحلیل دقیق‌تر BMR و TDEE، اطلاعات قد، وزن، جنسیت و سن باید کامل ثبت شده باشه. وقتی این داده‌ها کامل باشن، می‌شه کالری پایه بدن و کالری نگهدارنده روزانه رو دقیق‌تر بررسی کرد.';
  }
  return `BMR این رکورد حدود ${bmr.toLocaleString('en-US')} کالریه؛ یعنی بدن در حالت استراحت تقریباً همین مقدار انرژی برای کارهای پایه نیاز داره. TDEE هم با توجه به تعداد روزهای تمرین حدود ${tdee.toLocaleString('en-US')} کالری تخمین زده می‌شه؛ یعنی برای نگه داشتن وزن فعلی، مصرف روزانه باید حدود همین عدد باشه. اگر هدف کاهش سایز یا چربی‌سوزی باشه، باید کمی پایین‌تر از این عدد پیش بریم؛ اگر هدف عضله‌سازی یا جبران کم‌وزنی باشه، بهتره کالری و پروتئین کنترل‌شده بالاتر بره.`;
}

function ResultCard({ title, value, unit, tint }) {
  return (
    <article className={`${tint} ${CARD} p-3 text-center`}>
      <h4 className="text-[10px] font-black uppercase border-b-2 border-black pb-1.5 mb-1.5 tracking-widest">{title}</h4>
      <p className="seq-bmi-result-value text-xl font-black tabular-nums" dir="ltr">
        {value}
        {unit && <span className="block text-[10px] font-bold opacity-70 mt-0.5">{unit}</span>}
      </p>
    </article>
  );
}

function OverviewTab({ bmi, bucket, height, weight, activityLabel, report }) {
  return (
    <>
      <div className={`${bucket.fill} ${CARD} p-4 flex flex-col gap-3 relative overflow-hidden shrink-0`}>
        <div className="absolute -left-8 -top-8 opacity-20 transform -scale-x-100 pointer-events-none">
          <Gauge className="w-32 h-32 text-black" />
        </div>
        <div className="flex items-center gap-2 z-10">
          <div className="w-8 h-8 bg-black text-white flex items-center justify-center border-2 border-black rounded-lg">
            <Gauge className="w-4 h-4" />
          </div>
          <h3 className="text-base font-black tracking-tight">شاخص توده بدنی</h3>
        </div>
        <div className="z-10 text-center py-1">
          <div className="text-5xl font-black tabular-nums leading-none" dir="ltr">{bmi.toFixed(1)}</div>
          <span className={`${CHIP} inline-block mt-3`}>{bucket.label}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 z-10">
          <div className="bg-white border-2 border-black rounded-lg p-2 text-center">
            <span className="block text-[10px] font-black opacity-70">قد</span>
            <strong className="text-lg font-black tabular-nums">{Math.round(height)}</strong>
          </div>
          <div className="bg-white border-2 border-black rounded-lg p-2 text-center">
            <span className="block text-[10px] font-black opacity-70">وزن</span>
            <strong className="text-lg font-black tabular-nums">{Math.round(weight)}</strong>
          </div>
        </div>
        <p className="text-[11px] font-bold z-10 pt-2 border-t-2 border-black/20">
          سطح فعالیت انتخاب‌شده: <b>{activityLabel}</b>
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2.5 shrink-0">
        <ResultCard title="BMI" value={report.bmi.toFixed(1)} unit={report.bmiCat} tint={bucket.fill} />
        <ResultCard title="BMR" value={report.bmr} unit="کالری پایه" tint="bg-yellow-300" />
        <ResultCard title="TDEE" value={report.tdee} unit="کالری روزانه" tint="bg-emerald-300" />
      </div>
      <div className={`bg-white ${CARD} p-4 flex flex-col gap-3 shrink-0`}>
        <h3 className="text-base font-black tracking-tight">تفسیر نتایج</h3>
        {[
          { label: 'حفظ وزن', value: `${report.maintain}` },
          { label: 'کاهش وزن', value: `${report.lossLow} – ${report.lossHigh}` },
          { label: 'افزایش وزن', value: `${report.gainLow} – ${report.gainHigh}` }
        ].map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-3 p-3 bg-white border-2 border-black rounded-lg">
            <span className="font-black text-sm">{row.label}</span>
            <strong className="font-black text-sm tabular-nums" dir="ltr">{row.value}</strong>
          </div>
        ))}
      </div>
      <div className={`bg-white ${CARD} p-4 flex flex-col gap-3 shrink-0`}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-black text-white flex items-center justify-center border-2 border-black rounded-lg shrink-0">
            <Bot className="w-4 h-4" />
          </div>
          <h3 className="text-base font-black tracking-tight">تحلیل هوشمند سکویا</h3>
        </div>
        <p className="text-[13px] font-bold leading-7 text-black/80">
          {metabolicParagraph(report.bmi, report.bmr, report.tdee)}
        </p>
      </div>
    </>
  );
}

function GuideSection({ title, calories, protein, carbs, fat, proteinLabel, carbsLabel }) {
  const rows = [
    { label: 'نیاز کالری روزانه', value: formatNumber(calories), unit: 'کالری' },
    { label: `پروتئین (${proteinLabel})`, value: protein, unit: 'گرم' },
    { label: `کربوهیدرات (${carbsLabel})`, value: carbs, unit: 'گرم' },
    { label: 'چربی (۲۰ تا ۳۰٪ کل کالری)', value: fat, unit: 'گرم' }
  ];
  return (
    <section className={`bg-[#fee3c7] ${CARD} p-4 flex flex-col gap-3`}>
      <h4 className="text-sm font-black tracking-tight">{title}</h4>
      {rows.map((row) => (
        <div key={row.label} className="seq-bmi-guide-row bg-white border-2 border-black rounded-lg p-3">
          <span className="text-xs font-black leading-6">{row.label}</span>
          <strong className="font-black tabular-nums whitespace-nowrap" dir="ltr">{row.value} {row.unit}</strong>
        </div>
      ))}
    </section>
  );
}

function NutritionTab({ report, weight }) {
  const guide = useMemo(() => {
    const gainCalories = Math.round((report.gainLow + report.gainHigh) / 2);
    const lossCalories = Math.round((report.lossLow + report.lossHigh) / 2);
    const recompCalories = Math.max(report.bmr, report.tdee - 250);
    const maintainCalories = report.maintain;
    return {
      gain: {
        calories: gainCalories,
        protein: formatRange(weight, 1.6, 2.2),
        carbs: formatRange(weight, 3, 6),
        fat: fatRange(gainCalories)
      },
      loss: {
        calories: lossCalories,
        protein: formatRange(weight, 1.8, 2.4),
        carbs: formatRange(weight, 2, 4),
        fat: fatRange(lossCalories)
      },
      recomp: {
        calories: recompCalories,
        protein: formatRange(weight, 2.2, 2.5),
        carbs: formatRange(weight, 2, 4),
        fat: fatRange(recompCalories)
      },
      maintain: {
        calories: maintainCalories,
        protein: formatRange(weight, 1.2, 1.8),
        carbs: formatRange(weight, 3, 5),
        fat: fatRange(maintainCalories)
      }
    };
  }, [report, weight]);
  return (
    <div className="content-section seq-bmi-content-section">
      <div className={`bg-white ${CARD} p-4`}>
        <h3>راهنمای تغذیه بر اساس هدف</h3>
        <p>اعداد زیر بر اساس وزن و TDEE محاسبه‌شده‌ی شما هستند و به‌عنوان نقطه‌ی شروع کاربرد دارند.</p>
      </div>
      <GuideSection
        title="۱. عضله‌سازی (افزایش وزن)"
        {...guide.gain} proteinLabel="۱.۶ تا ۲.۲ گرم به ازای هر کیلوگرم"
        carbsLabel="۳ تا ۶ گرم به ازای هر کیلوگرم" />
      <GuideSection
        title="۲. چربی‌سوزی (کاهش وزن)"
        {...guide.loss} proteinLabel="۱.۸ تا ۲.۴ گرم به ازای هر کیلوگرم"
        carbsLabel="۲ تا ۴ گرم به ازای هر کیلوگرم" />
      <GuideSection
        title="۳. چربی‌سوزی و عضله‌سازی"
        {...guide.recomp} proteinLabel="۲.۲ تا ۲.۵ گرم به ازای هر کیلوگرم"
        carbsLabel="۲ تا ۴ گرم به ازای هر کیلوگرم" />
      <GuideSection
        title="۴. حفظ تناسب اندام"
        {...guide.maintain} proteinLabel="۱.۲ تا ۱.۸ گرم به ازای هر کیلوگرم"
        carbsLabel="۳ تا ۵ گرم به ازای هر کیلوگرم" />
      <p className="seq-bmi-notice">
        این بازه‌ها مستقل‌اند و قرار نیست کمینه یا بیشینه‌ی همه‌ی آن‌ها هم‌زمان جمع شود. TDEE و کالری هدف تخمینی‌اند؛ روند وزن، عملکرد تمرینی و شرایط پزشکی باید برای تنظیم دقیق‌تر در نظر گرفته شوند.
      </p>
    </div>
  );
}

function DefinitionsTab({ height }) {
  const heightSquared = (height / 100) ** 2;
  const weightAt = (bmi) => (bmi * heightSquared).toFixed(1);
  const weightRanges = {
    under: `کمتر از ${weightAt(18.5)}`,
    normal: `${weightAt(18.5)} – ${weightAt(24.9)}`,
    over: `${weightAt(25)} – ${weightAt(29.9)}`,
    obese1: `${weightAt(30)} – ${weightAt(34.9)}`,
    obese2: `${weightAt(35)} – ${weightAt(39.9)}`,
    obese3: `بیشتر از ${weightAt(40)}`
  };
  return (
    <div className="content-section seq-bmi-content-section">
      <section className={`bg-white ${CARD} p-4`}>
        <h3>تحلیل تخصصی BMI, BMR, TDEE</h3>
        <p>در این بخش، مفاهیم کلیدی مرتبط با متابولیسم و مدیریت وزن را به صورت جامع بررسی می‌کنیم.</p>
        <h4>شاخص توده بدنی (BMI)</h4>
        <p>BMI یک ابزار غربالگری اولیه است که وضعیت وزنی را بر اساس نسبت وزن به قد با فرمول <span className="formula"><span>BMI</span><span>=</span><span>وزن (kg)</span><span>÷</span><span>[قد (m)]²</span></span> ارزیابی می‌کند. این شاخص در سطح کلان کاربرد دارد اما برای ارزیابی فردی، به خصوص برای ورزشکاران، محدودیت‌های جدی دارد زیرا تفاوتی بین توده چربی و عضلانی قائل نمی‌شود.</p>
        <div className="seq-bmi-table-wrap">
          <table>
            <thead><tr><th>وضعیت وزنی</th><th>BMI (kg/m²)</th><th>محدوده وزن شما (kg)</th></tr></thead>
            <tbody>
              <tr><td>کم‌وزن</td><td>کمتر از 18.5</td><td>{weightRanges.under}</td></tr>
              <tr><td>نرمال</td><td>18.5 تا 24.9</td><td>{weightRanges.normal}</td></tr>
              <tr><td>اضافه‌وزن</td><td>25 تا 29.9</td><td>{weightRanges.over}</td></tr>
              <tr><td>چاقی درجه ۱</td><td>30 تا 34.9</td><td>{weightRanges.obese1}</td></tr>
              <tr><td>چاقی درجه ۲</td><td>35 تا 39.9</td><td>{weightRanges.obese2}</td></tr>
              <tr><td>چاقی شدید</td><td>بالای 40</td><td>{weightRanges.obese3}</td></tr>
            </tbody>
          </table>
        </div>
        <p className="orng-notice">* برای ارزیابی دقیق‌تر ترکیب بدنی، به خصوص در افراد ورزشکار، باید به سراغ شاخص‌های پیشرفته‌تری رفت. دلیل اصلی این است که بافت عضلانی به مراتب چگال‌تر و سنگین‌تر از بافت چربی است. برای مثال، یک بدنساز حرفه‌ای با توده عضلانی بسیار بالا ممکن است شاخص BMI بالای ۳۰ داشته باشد که طبق جدول، او را در دسته «چاق» قرار می‌دهد، در حالی که درصد چربی بدن او بسیار پایین و وضعیت سلامتی‌اش ایده‌آل است. در چنین مواردی، BMI تصویری کاملاً گمراه‌کننده ارائه می‌دهد زیرا وزن بالای ناشی از عضله را از چربی اضافی تشخیص نمی‌دهد. به همین دلیل، معیارهایی مانند درصد چربی بدن (Body Fat Percentage)، نسبت دور کمر به باسن (Waist-to-Hip Ratio) و اسکن‌های تخصصی مانند DEXA یا InBody که مستقیماً ترکیب بدنی را تحلیل می‌کنند، ابزارهای به مراتب قابل‌اعتمادتری هستند.</p>
        <h4>نرخ متابولیسم پایه (BMR)</h4>
        <p>BMR حداقل انرژی مورد نیاز بدن برای حفظ عملکردهای حیاتی در حالت استراحت کامل است. این انرژی صرف فرآیندهایی مانند تنفس، گردش خون، و فعالیت ارگان‌های حیاتی می‌شود. BMR بزرگترین بخش (حدود ۶۰-۷۵٪) از کل کالری مصرفی روزانه شما را تشکیل می‌دهد و تحت تأثیر عواملی چون ترکیب بدنی (عضله در مقابل چربی)، سن، جنسیت، ژنتیک و وضعیت هورمونی (مانند تیروئید) قرار دارد.</p>
        <h4>کل انرژی مصرفی روزانه (TDEE)</h4>
        <p>TDEE تصویر کامل کالری‌هایی است که بدن در یک شبانه‌روز می‌سوزاند و از مجموع اجزای زیر تشکیل شده است:</p>
        <ul>
          <li><strong>BMR:</strong> متابولیسم پایه.</li>
          <li><strong>ضریب فعالیت:</strong> به طور کلی به میزان فعالیت بدنی فرد در طول روز اشاره دارد. این ضریب برای محاسبه کالری مورد نیاز روزانه و تعیین برنامه تمرینی مناسب استفاده می‌شود و نشان می‌دهد که یک فرد در طول روز چقدر فعالیت دارد، از کمترین (نشسته) تا بیشترین (بسیار فعال).</li>
        </ul>
        <p>فرمول ساده‌شده <span className="formula"><span>TDEE</span><span>=</span><span>BMR</span><span>×</span><span>ضریب فعالیت</span></span> این اجزا را برای تخمینی سریع خلاصه می‌کند. <b>ضریب فعالیت (Activity Factor)</b> نشان‌دهنده سبک زندگی شماست:</p>
        <ul>
          <li><b>خیلی کم‌تحرک (ضریب ۱.۲):</b> فقط نشستن و خوابیدن، بدون ورزش.</li>
          <li><b>کم‌تحرک (ضریب ۱.۳۷۵):</b> کمی ورزش یا پیاده‌روی سبک ۱-۳ روز در هفته.</li>
          <li><b>متوسط (ضریب ۱.۵۵):</b> تمرین با شدت متوسط ۳-۵ روز در هفته.</li>
          <li><b>فعال (ضریب ۱.۷۲۵):</b> تمرین شدید یا کار فیزیکی ۶-۷ روز در هفته.</li>
          <li><b>بسیار فعال (ضریب ۱.۹):</b> ورزشکار حرفه‌ای یا مشاغل فیزیکی بسیار سنگین.</li>
        </ul>
        <hr />
        <h3>راهنمای کامل تغذیه برای عضله‌سازی (هایپرتروفی)</h3>
        <p>برای رسیدن به هایپرتروفی (رشد عضلات)، تمرین به‌تنهایی کافی نیست. رژیم غذایی باید به‌صورت هدفمند تنظیم شود.</p>
        <h4>۱. کالری روزانه</h4>
        <p>برای کاهش وزن، شما باید در وضعیت <strong>کسری کالری (Caloric Deficit)</strong> باشید، یعنی کالری کمتری نسبت به <strong>TDEE</strong> خود دریافت کنید. کسری کالری به میزان <strong>۱۵ تا ۲۰ درصد کمتر از TDEE</strong>، بهترین و پایدارترین روش برای چربی‌سوزی است. این محدوده به اندازه کافی مؤثر است تا کاهش وزن مداوم را تضمین کند، اما آنقدر شدید نیست که باعث تحلیل عضلانی، افت شدید انرژی یا گرسنگی شدید شود.</p>
        <p>برای افزایش وزن و عضله‌سازی، شما باید در وضعیت <strong>کالری مازاد (Caloric Surplus)</strong> باشید تا بدن انرژی اضافی برای ساخت بافت جدید را داشته باشد. افزایش کالری به میزان <strong>۱۰ تا ۱۵ درصد بیشتر از TDEE</strong>، محدوده ایده‌آل است. این مقدار، انرژی کافی برای رشد عضلات را فراهم کرده و همزمان افزایش چربی اضافی را به حداقل می‌رساند.</p>
        <h4>۲. پروتئین – ساختار عضله</h4>
        <p>بر اساس فرمول <span className="formula"><span>وزن (kg)</span><span>×</span><span>1.6</span><span>تا</span><span>2.2</span></span>، پروتئین مورد نیاز خود را محاسبه کنید. این مقدار را در 3 تا 5 وعده در طول روز پخش کنید.</p>
        <h4>۳. کربوهیدرات – سوخت عضله</h4>
        <p>با فرمول <span className="formula"><span>وزن (kg)</span><span>×</span><span>3</span><span>تا</span><span>6</span></span> کربوهیدرات لازم برای پر کردن ذخایر گلیکوژن و افزایش عملکرد را تامین کنید.</p>
        <h4>۴. چربی – تعادل هورمونی</h4>
        <p>حدود <span className="formula seq-bmi-formula-rtl"><span>20%</span><span>تا</span><span>30%</span><span>از</span><span>کل کالری</span></span> را به چربی‌های سالم اختصاص دهید که برای تولید هورمون‌ها و ریکاوری ضروری هستند.</p>
        <h4>۵. ریزمغذی‌های کلیدی</h4>
        <div className="seq-bmi-table-wrap">
          <table>
            <thead><tr><th>ماده</th><th>مقدار روزانه</th><th>منابع</th></tr></thead>
            <tbody>
              <tr><td>ویتامین D3</td><td>1000–2000 IU</td><td>نور خورشید، ماهی چرب</td></tr>
              <tr><td>منیزیم</td><td>300–400 mg</td><td>سبزیجات، مغزها، دانه‌ها</td></tr>
              <tr><td>ید</td><td>150 µg</td><td>نمک یددار، غذاهای دریایی</td></tr>
            </tbody>
          </table>
        </div>
        <p className="seq-bmi-notice">نیاز به مکمل بر اساس سن، وضعیت سلامت، رژیم غذایی و نتایج آزمایش متفاوت است؛ مصرف مکمل را با پزشک یا متخصص تغذیه هماهنگ کنید.</p>
      </section>
    </div>
  );
}

function BmiCoachNotice({ onConfirm }) {
  return (
    <section className="bg-white border-3 border-black rounded-xl p-5 text-right text-[#1f2937]" role="note" aria-label="پیام مربی درباره شاخص توده بدنی">
      <span className="inline-flex rounded-full bg-[#e06d02] px-3 py-1 text-[11px] font-extrabold text-white">پیام مربی</span>
      <p className="mt-4 text-[13px] font-medium leading-7">
        قبل از بررسی روند پیشرفت لازم به ذکره تمامی تحلیل‌ها و برچسب‌گذاری‌های مرتبط با وزن/قد و پارمتر BMI لزوما کامل و صحیح نیستن.
        <br /><br />
        این دست تحلیل‌ها، برای ورزشکاران با سبک زندگی معمولی تا حد قابل قبولی نزدیک به واقعیت هستن.{' '}
        <strong>اما برای ورزشکاران حرفه‌ای با توده‌ی عضلانی بالا به دلیل اینکه این پارمتر در روند محاسبات BMI دخیل نیست، به احتمال زیاد با خطای محاسباتی همراه هست.</strong>
        <br /><br />
        به همین دلیله که عدد روی ترازو نباید تنها معیارت برای سنجش پیشرفتت باشه.
        <br />
        پایدار باشی 💚
      </p>
      <button type="button" onClick={onConfirm} className="mt-5 rounded-xl bg-[#e06d02] px-6 py-3 text-sm font-extrabold text-white seq-bmi-raised active:translate-y-0.5 cursor-pointer">
        باشه
      </button>
    </section>
  );
}

export default function BmiResultDock({ bmi, bucket, height, weight, activityLabel, report }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [noticeOpen, setNoticeOpen] = useState(true);
  if (!report) return null;

  return (
    <div className="bg-[#f8f8f8] border-3 border-black rounded-2xl w-full flex flex-col overflow-hidden" dir="rtl" aria-label="نتیجه تحلیل بدن">
      <div className="bg-yellow-300 border-b-4 border-black px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 bg-black text-white flex items-center justify-center rounded-xl border-2 border-black">
          <Flame className="w-5 h-5 text-yellow-300" />
        </div>
        <h2 className="text-lg md:text-xl font-black tracking-tight">تحلیل جامع بدن</h2>
      </div>
      <div className="seq-bmi-tabs" role="tablist" aria-label="بخش‌های نتیجه">
        {TABS.map((tab) => (
          <button key={tab.id} type="button" role="tab" id={`seq-bmi-tab-${tab.id}`} aria-selected={activeTab === tab.id} aria-controls={`seq-bmi-panel-${tab.id}`} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>
      <div className="p-4 md:p-6 flex flex-col gap-5" dir="rtl" role="tabpanel" id={`seq-bmi-panel-${activeTab}`} aria-labelledby={`seq-bmi-tab-${activeTab}`}>
        {noticeOpen && <BmiCoachNotice onConfirm={() => setNoticeOpen(false)} />}
        {activeTab === 'overview' && <OverviewTab bmi={bmi} bucket={bucket} height={height} weight={weight} activityLabel={activityLabel} report={report} />}
        {activeTab === 'nutrition' && <NutritionTab report={report} weight={weight} />}
        {activeTab === 'definitions' && <DefinitionsTab height={height} />}
      </div>
    </div>
  );
}
